'use client';

import { useEffect, useState } from 'react';
import { Award } from 'lucide-react';
import NumberFlow from '@number-flow/react';
import { apiFetch } from '@/lib/api';
import DashboardMetricCard from '../DashboardMetricCard';
import { BadgeArt } from '../../badgeIcons';

type Badge = {
  slug: string;
  name: string;
  icon: string;
  earnedAt: string;
  expiresAt: string | null;
  ownedPercent?: number | null;
  imageUrl?: string | null;
};

type ProgressItem = {
  slug: string;
  name: string;
  icon: string;
  progress: number;
  target: number;
  label: string;
};

type Response = {
  earned: Badge[];
  progress: ProgressItem[];
};

export default function BadgesCard() {
  const [data, setData] = useState<Response | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/profile/me/badges-with-progress', undefined, { method: 'GET' });
        if (!res.ok) { if (!cancelled) setError(true); return; }
        const j = (await res.json()) as Response;
        if (!cancelled) setData(j);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const icon = <Award className="h-4 w-4" strokeWidth={1.75} />;

  if (error) {
    return (
      <DashboardMetricCard title="Badges" icon={icon}>
        <div className="text-sm text-muted-foreground">Couldn&apos;t load badges right now.</div>
      </DashboardMetricCard>
    );
  }

  if (data === null) {
    return (
      <DashboardMetricCard title="Badges" href="/profile/badges" icon={icon}>
        <div className="space-y-3">
          <div className="h-9 w-12 rounded bg-muted animate-pulse" />
          <div className="h-3 w-32 rounded bg-muted/60 animate-pulse" />
        </div>
      </DashboardMetricCard>
    );
  }

  const earnedCount = data.earned.length;
  const top3 = data.earned.slice(0, 3);
  // Surface the closest-to-completion next badge (highest progress/target ratio)
  const next = data.progress
    .filter((p) => p.target > 0)
    .sort((a, b) => (b.progress / b.target) - (a.progress / a.target))[0];

  return (
    <DashboardMetricCard title="Badges" href="/profile/badges" icon={icon}>
      <div className="font-serif text-3xl font-semibold tabular-nums">
        <NumberFlow value={earnedCount} />
      </div>
      {top3.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {top3.map((b) => (
            <span
              key={b.slug}
              title={b.name}
              className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-xs font-medium ring-chip"
            >
              <BadgeArt icon={b.icon} imageUrl={b.imageUrl} sizePx={24} className="h-3 w-3" />
              {b.name}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground mt-1">
          None earned yet. Post, comment, or sign in tomorrow to earn your first.
        </div>
      )}
      {next ? (
        <div className="mt-3">
          <div className="text-xs text-muted-foreground">
            Next: {next.name} — {next.label}
          </div>
          <div className="mt-1.5 h-1 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary"
              style={{ width: `${Math.round((next.progress / next.target) * 100)}%` }}
            />
          </div>
        </div>
      ) : null}
    </DashboardMetricCard>
  );
}
